import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { DashboardLayoutTemplateUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type DashboardLayoutTemplateUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  forRole: string;
  forRoleSelectedValue?: { displayId: string; id: string } | null;
  onForRoleChange?: (value: string) => void;
  forRoleError?: string;
  widgets: string;
  widgetsSelectedValue?: { displayId: string; id: string } | null;
  onWidgetsChange?: (value: string) => void;
  widgetsError?: string;
  isDefault: boolean;
  isDefaultSelectedValue?: { displayId: string; id: string } | null;
  onIsDefaultChange?: (value: boolean) => void;
  isDefaultError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  isForAllTenants: boolean;
  isForAllTenantsSelectedValue?: { displayId: string; id: string } | null;
  onIsForAllTenantsChange?: (value: boolean) => void;
  isForAllTenantsError?: string;
  dashboardLayoutTemplateUpdateStore: DashboardLayoutTemplateUpdateStore;
};

export const DashboardLayoutTemplateUpdateForm: React.FC<DashboardLayoutTemplateUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      forRole = "",
      forRoleError,
      onForRoleChange = () => {},
      widgets = "{}",
      widgetsError,
      onWidgetsChange = () => {},
      isDefault = false,
      isDefaultError,
      onIsDefaultChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      isForAllTenants = false,
      isForAllTenantsError,
      onIsForAllTenantsChange = () => {},
      dashboardLayoutTemplateUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="DashboardLayoutTemplate"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "forRole":
                    onForRoleChange?.(valor);
                    break;
                  case "widgets":
                    onWidgetsChange?.(valor);
                    break;
                  case "isDefault":
                    onIsDefaultChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "isForAllTenants":
                    onIsForAllTenantsChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={dashboardLayoutTemplateUpdateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupDashboardLayoutTemplateLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(dashboardLayoutTemplateUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    dashboardLayoutTemplateUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.DashboardLayoutTemplateNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.DashboardLayoutTemplateNameDescription}
                />
                <InputString
                  value={forRole}
                  error={Boolean(forRoleError)}
                  warning={
                    !forRoleError &&
                    Boolean(
                      dashboardLayoutTemplateUpdateStore.warnings["forRole"]
                    )
                  }
                  helperText={
                    forRoleError ||
                    dashboardLayoutTemplateUpdateStore.warnings["forRole"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateForRoleLabel}
                  onChange={(value: string) => onForRoleChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.DashboardLayoutTemplateForRoleDescription
                  }
                />
                <InputCodeEditor
                  value={widgets}
                  error={Boolean(widgetsError)}
                  helperText={
                    widgetsError ||
                    dashboardLayoutTemplateUpdateStore.warnings["widgets"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateWidgetsLabel}
                  onChange={(value: string) => onWidgetsChange(value)}
                  language="json"
                  description={
                    langStore.DashboardLayoutTemplateWidgetsDescription
                  }
                />
                <InputBoolean
                  value={isDefault}
                  error={Boolean(isDefaultError)}
                  helperText={
                    isDefaultError ||
                    dashboardLayoutTemplateUpdateStore.warnings["isDefault"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateIsDefaultLabel}
                  description={
                    langStore.DashboardLayoutTemplateIsDefaultDescription
                  }
                  onChange={(value: boolean) => onIsDefaultChange(value)}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    dashboardLayoutTemplateUpdateStore.warnings["enabled"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateEnabledLabel}
                  description={
                    langStore.DashboardLayoutTemplateEnabledDescription
                  }
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputBoolean
                  value={isForAllTenants}
                  error={Boolean(isForAllTenantsError)}
                  helperText={
                    isForAllTenantsError ||
                    dashboardLayoutTemplateUpdateStore.warnings[
                      "isForAllTenants"
                    ]?.message
                  }
                  label={langStore.DashboardLayoutTemplateIsForAllTenantsLabel}
                  onChange={(value: boolean) => onIsForAllTenantsChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
